import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Users, ArrowUpRight, Share2, Trash2 } from "lucide-react";
import TrendingRail from "@/components/TrendingRail";

export default function Lobby() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // room object or null
  const [creationResult, setCreationResult] = useState(null); // {status, name, reason} or null
  const [form, setForm] = useState({ name: "", topic: "", color: "#D4FF00" });

  const load = async () => {
    try {
      const [{ data: all }, { data: mine }] = await Promise.all([
        api.get("/rooms"),
        api.get("/rooms/mine").catch(() => ({ data: [] })),
      ]);
      setRooms(all);
      setMyRooms(mine);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/rooms", form);
      setShowCreate(false);
      setForm({ name: "", topic: "", color: "#D4FF00" });
      setCreationResult(data);
      if (data.status === "approved") {
        toast.success("ROOM APPROVED · LIVE NOW");
      } else if (data.status === "rejected") {
        toast.error("ROOM REJECTED BY MOD");
      } else {
        toast("ROOM PENDING REVIEW");
      }
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const totalLive = rooms.reduce((s, r) => s + (r.live_count || 0), 0);
  const isGuest = !!user?.is_guest;
  const myActive = myRooms.filter((r) => r.status !== "rejected").length;
  const guestBlocked = isGuest && myActive >= 1;
  const pendingOrRejected = myRooms.filter((r) => r.status && r.status !== "approved");

  const shareRoom = async (e, slug) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/share/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("SHARE LINK COPIED");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const askDelete = (e, room) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDelete(room);
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/rooms/${confirmDelete.slug}`);
      toast.success(`ROOM ${confirmDelete.name} DELETED`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />

      {/* Marquee ticker */}
      <div className="border-b border-white/10 bg-panel overflow-hidden">
        <div className="marquee-track py-2 text-acid font-mono text-xs tracking-widest whitespace-nowrap">
          {Array(2).fill(0).map((_, i) => (
            <span key={i} className="mx-8">
              LIVE {totalLive} SOULS · {rooms.slice(0, 5).map(r => `${r.name} [${r.live_count}]`).join(" · ")} · JACK IN ANYWHERE ·
            </span>
          ))}
        </div>
      </div>

      <TrendingRail />

      <main className="px-4 md:px-8 py-8 md:py-12 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono text-acid text-xs tracking-widest">// LOBBY</div>
            <h1 className="font-heading text-5xl md:text-7xl leading-none mt-2">
              PICK<br /><span className="text-acid">A ROOM.</span>
            </h1>
            <p className="font-mono text-white/60 text-xs mt-4 max-w-md">
              &gt; Rooms are always open. Walk in mid-conversation. Nobody&apos;s waiting for you to arrive.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-invite-friend"
              onClick={async () => {
                const { shareLink } = await import("@/lib/share");
                const backend = process.env.REACT_APP_BACKEND_URL || "";
                const url = `${backend}/api/share/room/late-night`;
                const res = await shareLink({
                  title: "Come chat on NITEWIRE",
                  text: "Always-live chat rooms — astrology, dating, business ideas, romance, gaming. Walk in mid-conversation:",
                  url,
                });
                if (res.method === "copy" && res.ok) toast.success("INVITE LINK COPIED");
                else if (res.method === "copy") toast.error("Copy failed.");
                else if (res.method === "native" && res.ok) toast.success("SHARED");
              }}
              className="font-mono text-xs px-4 py-2 border-2 border-white/20 hover:border-acid hover:text-acid uppercase tracking-widest flex items-center gap-2"
              title="Invite a friend"
            >
              <Share2 className="w-4 h-4" /> Invite friend
            </button>
            <button
              data-testid="btn-create-room"
              onClick={() => guestBlocked ? toast.error("Guest limit: 1 room. Register for unlimited.") : setShowCreate(true)}
              className="btn-acid flex items-center gap-2"
              disabled={guestBlocked}
              title={guestBlocked ? "Guest limit reached — register for unlimited rooms" : "Create a new room"}
            >
              <Plus className="w-4 h-4" /> NEW ROOM
            </button>
          </div>
        </div>

        {loading && <div className="font-mono text-acid">LOADING…</div>}

        {pendingOrRejected.length > 0 && (
          <section className="mb-8 border border-white/15" data-testid="my-pending-rooms">
            <div className="px-4 py-3 border-b border-white/15 flex items-center justify-between">
              <div className="font-mono text-[10px] tracking-widest text-white/60 uppercase">// YOUR SUBMISSIONS</div>
              <div className="font-mono text-[10px] text-white/40">only you see these</div>
            </div>
            <div className="divide-y divide-white/10">
              {pendingOrRejected.map((r) => {
                const rejected = r.status === "rejected";
                return (
                  <div key={r.slug} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span
                      className="font-mono text-[10px] tracking-widest px-2 py-0.5 border"
                      style={{ color: rejected ? "#FF3B30" : "#D4FF00", borderColor: rejected ? "#FF3B30" : "#D4FF00" }}
                    >
                      {r.status.toUpperCase()}
                    </span>
                    <div className="font-heading text-2xl leading-none">{r.name}</div>
                    <div className="font-body text-white/60 text-sm flex-1 min-w-0 truncate">
                      {rejected ? (r.moderation_reason || "Rejected by moderator.") : "Waiting for approval."}
                    </div>
                    <button
                      onClick={() => setConfirmDelete(r)}
                      data-testid={`delete-room-${r.slug}`}
                      className="text-white/40 hover:text-blaze p-1"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-white/10" data-testid="rooms-grid">
          {rooms.map((r, idx) => {
            // Bento-ish: make first row and every 7th larger
            const big = idx === 0 || (idx > 0 && idx % 7 === 0);
            return (
              <Link
                key={r.slug}
                to={`/room/${r.slug}`}
                data-testid={`room-${r.slug}`}
                className={`group relative p-6 md:p-8 border-r border-b border-white/10 hover:bg-panel transition-colors ${big ? "sm:col-span-2 lg:col-span-2 lg:row-span-1" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-[10px] tracking-widest" style={{ color: r.color }}>
                      {r.preset ? "PRESET" : `BY @${r.created_by}`}
                    </div>
                    <h2 className={`font-heading leading-none mt-2 ${big ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
                      {r.name}
                    </h2>
                    <p className="font-body text-white/60 text-sm mt-3 max-w-sm">{r.topic}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <button
                      onClick={(e) => shareRoom(e, r.slug)}
                      data-testid={`share-room-${r.slug}`}
                      title="Copy share link"
                      className="text-white/30 hover:text-acid p-1"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    {!r.preset && user?.username && r.created_by === user.username && (
                      <button
                        onClick={(e) => askDelete(e, r)}
                        data-testid={`delete-room-${r.slug}`}
                        title="Delete this room"
                        className="text-white/30 hover:text-blaze p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-acid transition-colors" />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-8">
                  <div className="flex items-center gap-2 font-mono text-xs">
                    <div className={`w-2 h-2 ${r.live_count > 0 ? "bg-acid animate-pulse" : "bg-white/20"}`} />
                    <Users className="w-3 h-3" />
                    <span className="text-white/80">{r.live_count} LIVE</span>
                  </div>
                  <span className="font-mono text-[10px] text-white/30 tracking-widest">/{r.slug}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </main>

      {showCreate && (
        <div className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-obsidian border-2 border-acid w-full max-w-md p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-acid text-xs tracking-widest">// NEW ROOM</div>
            <h3 className="font-heading text-4xl mt-2">SPIN ONE UP.</h3>
            <form onSubmit={create} className="mt-6 space-y-4" data-testid="create-room-form">
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/60">NAME</label>
                <input data-testid="input-room-name" className="input-brutal mt-1" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={40} />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/60">TOPIC</label>
                <input data-testid="input-room-topic" className="input-brutal mt-1" value={form.topic}
                       onChange={(e) => setForm({ ...form, topic: e.target.value })} required maxLength={140} />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-white/60">ACCENT</label>
                <div className="flex gap-2 mt-2">
                  {["#D4FF00", "#FF3B30", "#00E5FF", "#F4F4F0"].map(c => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                            className={`w-8 h-8 border-2 ${form.color === c ? "border-white" : "border-white/20"}`}
                            style={{ background: c }} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" className="btn-brutal flex-1" onClick={() => setShowCreate(false)}>CANCEL</button>
                <button type="submit" className="btn-acid flex-1" data-testid="submit-create-room">CREATE</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-modal">
          <div className="bg-obsidian border-2 border-blaze w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-blaze text-xs tracking-widest">// DELETE ROOM</div>
            <h3 className="font-heading text-4xl mt-2">BURN IT DOWN?</h3>
            <p className="font-mono text-white/70 text-xs mt-3">
              &gt; This deletes <span className="text-acid">{confirmDelete.name}</span>, all its messages, and boots anyone still inside. No undo.
            </p>
            <div className="flex gap-3 mt-6">
              <button className="btn-brutal flex-1" onClick={() => setConfirmDelete(null)}>CANCEL</button>
              <button
                data-testid="confirm-delete-btn"
                onClick={doDelete}
                className="btn-acid flex-1"
                style={{ background: "#FF3B30", borderColor: "#FF3B30", color: "#050505" }}
              >
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}
      {creationResult && (
        <div
          className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4"
          onClick={() => setCreationResult(null)}
          data-testid="creation-result-modal"
        >
          <div
            className="bg-obsidian border-2 w-full max-w-md p-6"
            style={{ borderColor: creationResult.status === "rejected" ? "#FF3B30" : "#D4FF00" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="font-mono text-xs tracking-widest"
              style={{ color: creationResult.status === "rejected" ? "#FF3B30" : "#D4FF00" }}
            >
              // {creationResult.status === "approved" ? "LIVE" : creationResult.status === "rejected" ? "REJECTED" : "PENDING REVIEW"}
            </div>
            <h3 className="font-heading text-4xl mt-2" data-testid="creation-result-title">
              {creationResult.status === "approved"
                ? "YOU'RE LIVE."
                : creationResult.status === "rejected"
                ? "MOD SAID NO."
                : "WITH THE MOD."}
            </h3>
            <p className="font-mono text-white/70 text-xs mt-3">
              {creationResult.status === "approved" ? (
                <>&gt; <span className="text-acid">{creationResult.name}</span> is open. Anyone can walk in.</>
              ) : creationResult.status === "rejected" ? (
                <>&gt; The AI mod flagged <span className="text-acid">{creationResult.name}</span>. Reason:
                <span className="block mt-2 text-blaze">{creationResult.moderation_reason || "policy violation"}</span>
                Only you can see this room. Delete it below.</>
              ) : (
                <>&gt; The AI mod is reviewing <span className="text-acid">{creationResult.name}</span>. Only you can enter until it&apos;s live. This usually takes a few seconds.</>
              )}
            </p>
            <div className="flex gap-3 mt-6">
              <button
                data-testid="creation-result-close"
                className="btn-brutal flex-1"
                onClick={() => setCreationResult(null)}
              >
                GOT IT
              </button>
              {creationResult.status === "approved" && (
                <button
                  data-testid="creation-result-open"
                  className="btn-acid flex-1"
                  onClick={() => { setCreationResult(null); window.location.href = `/room/${creationResult.slug}`; }}
                >
                  WALK IN
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
